import { User } from 'models/user';
import { ConnectedLab, Hours, Organization } from 'models/organization';
import { OrganizationContact } from 'models/contact';
import { TotalAmount, Invoice } from './invoice';
import { OrderStep, Time } from './workflow-step';
import { DateRange } from '@mui/lab';
import { File as DenthubFile } from './file';
import { TransportOrder } from './transport';
import { OrderTag } from './tags';
import { OrderForm } from './order-form';
import { NShiftShipmentWaybill } from './nshift-shipment-waybill';
import { ScannerCase } from './scanner-case';
import { AllAdditionalInfo } from './order-input/shared';
import { OrderCameInVia } from './production-overview';

export type WorkFlow = {
    _id: string;
    timeInCurrentStep: Time;
    currentStep: OrderStep;
    stepLogs: OrderStep[];
};

export type Certified = {
    on: number;
    by: User;
};

export type LinkedUser = {
    user: User;
    dateAdded: number;
};

export type LinkedClinic = {
    clinic: Organization;
    dateAdded: number;
};

export enum OrderOnDateFilter {
    NO_DATE_FILTER = 'NO_DATE_FILTER',
    ORDER_CREATED = 'ORDER_CREATED',
    ORDER_DELIVERED = 'ORDER_DELIVERED',
    ORDER_RECEIVED = 'ORDER_RECEIVED',
    ORDER_SENT = 'ORDER_SENT',
    DEADLINE = 'DEADLINE',
    DELIVERY_DATE = 'DELIVERY_DATE',
    PRODUCTION_FINISHED = 'PRODUCTION_FINISHED',
}

// DD-3387: invoicing choice for a per-order-invoice clinic in the send dialog.
export enum PerOrderSendInvoiceAction {
    DO_NOTHING = 'DO_NOTHING',
    CREATE_INVOICE = 'CREATE_INVOICE',
    SHOULD_NOT_INVOICE = 'SHOULD_NOT_INVOICE',
}

/**
 * DD-3391: the invoicing choices an elevated external user has when they send an
 * order. Mirrors the `ExternalUserInvoiceAction` enum in the schema: the
 * collective states, plus leaving invoicing alone. Creating the invoice of a
 * per-order clinic stays with the lab, so it is not one of these.
 */
export enum ExternalUserInvoiceAction {
    NOT_READY = 'NOT_READY',
    READY_FOR_INVOICE = 'READY_FOR_INVOICE',
    SHOULD_NOT_INVOICE = 'SHOULD_NOT_INVOICE',
    DO_NOTHING = 'DO_NOTHING',
}

// DD-3463: batch actions.
export enum BatchOrderInvoiceGroup {
    COLLECTIVE = 'COLLECTIVE',
    PER_ORDER = 'PER_ORDER',
    NO_INVOICE = 'NO_INVOICE',
}

export enum BatchPerOrderInvoiceAction {
    DO_NOTHING = 'DO_NOTHING',
    CREATE_INVOICE = 'CREATE_INVOICE',
}

export type BatchOrderRef = { _id: string; orderId: string };
export type BatchSkippedOrder = BatchOrderRef & { reason: string };
export type BatchOrderFailure = BatchOrderRef & { message: string };

export type BatchOrdersResult = {
    deliveryNoteBatchId?: string | null;
    succeeded: BatchOrderRef[];
    failed: BatchOrderFailure[];
    skipped: BatchSkippedOrder[];
};

export type BatchSendPreviewOrder = BatchOrderRef & { invoiceGroup: BatchOrderInvoiceGroup };

export type BatchSendOrdersPreview = {
    eligible: BatchSendPreviewOrder[];
    skipped: BatchSkippedOrder[];
    isPerOrderInvoiceDisabled: boolean;
};

export type BatchProductionFinishedPreview = {
    eligible: BatchOrderRef[];
    skipped: BatchSkippedOrder[];
};

// DD-3592: recent delivery-note batches, for re-download and re-running failures.
export enum DeliveryNoteBatchAction {
    PRODUCTION_FINISHED = 'PRODUCTION_FINISHED',
    SEND_TO_CLINIC = 'SEND_TO_CLINIC',
}

export enum DeliveryNoteBatchStatus {
    PROCESSING = 'PROCESSING',
    MERGING = 'MERGING',
    COMPLETED = 'COMPLETED',
    PARTIAL = 'PARTIAL',
    FAILED = 'FAILED',
}

export type RecentBatchCompletedOrder = { order: string; orderId: string };
export type RecentBatchFailedOrder = { order: string; orderId: string; errorMessage: string };

export type RecentDeliveryNoteBatch = {
    _id: string;
    action?: DeliveryNoteBatchAction | null;
    isRerun: boolean;
    createdAt: number;
    completedAt?: number | null;
    status?: DeliveryNoteBatchStatus | null;
    total: number;
    completedOrders: RecentBatchCompletedOrder[];
    failedOrders: RecentBatchFailedOrder[];
    downloadUrl?: string | null;
};

// skipped holds the failed orders that no longer exist and could not be re-run.
export type RerunDeliveryNoteBatchResult = {
    deliveryNoteBatchId: string;
    total: number;
    skipped: BatchOrderRef[];
};

export enum InvoicingState {
    'NOT_READY' = 'NOT_READY',
    'READY_FOR_INVOICE' = 'READY_FOR_INVOICE',
    'SHOULD_NOT_INVOICE' = 'SHOULD_NOT_INVOICE',
    'INVOICED' = 'INVOICED',
}

export enum OrderRejectStatus {
    DECLINED = 'DECLINED',
    CANCELLED = 'CANCELLED',
}

type Archived = {
    by: string[];
    byCustomer: boolean;
    byLab: boolean;
};

export type Order = {
    _id: string;
    orderId: string;
    currency: string;
    attachedScannerCaseFiles?: ScannerCase[];
    additionalInfo?: string;
    /** The journal system's own reference (Customer API / prefill link). Not shown in the UI. */
    vendorReference?: string;
    scanCaseId?: string;
    internalOrderId: string;
    type: OrderType;
    status: OrderStatus;
    unreadMessagesCount: number;
    // The responsible user at the ordering party: a dentist at a clinic, a
    // technician at a lab placing a supplier order. Absent on lab-to-lab
    // sub-orders created without one (DD-3494).
    dentist?: User;
    // DD-3473: clinic === customer and lab === supplier; the stored clinic/lab
    // fields are being removed, so only customer/supplier are read here.
    supplier: Organization;
    customer: Organization;
    deliveryDetails: OrderDeliveryDetails;
    created: {
        on: number;
        by: User;
    };
    originalCreationDate: Date;
    approvedBy?: {
        fullName: string;
        id: string;
    };
    draftCreatedAt?: number;
    draftUpdatedAt?: number;
    draftCreatedBy?: User;
    // Absent when the ordering lab withheld the patient details from the
    // subcontractor (DD-3494); the order's own orderId is the case handle.
    // DD-3620: absent for a restricted external user — the server withholds
    // patient identity rather than relying on the browser to hide it.
    patient?: OrderPatient;
    constructionMaterials: string[];
    isModify: boolean;
    labFlag: boolean;
    historicalComments: AllAdditionalInfo[];
    currentPickup?: TransportOrder;
    currentDelivery?: TransportOrder | NShiftShipmentWaybill | any;
    linkedExtTechs: LinkedUser[];
    linkedDentists: LinkedUser[];
    linkedClinics?: LinkedClinic[];
    customParticipantEmails?: string[];
    workFlow: WorkFlow;
    isCertified: boolean;
    certified: Certified;
    totalAmount?: TotalAmount;
    isProductionFinished: boolean;
    invoice: Invoice;
    invoicingState?: InvoicingState;
    invoiceActionTime?: Date | null;
    invoiceCreatedTime?: Date;
    invoicingStateChangedAt?: Date;
    invoicingStateChangedBy?: string | User;
    isCreatedByLab?: boolean;
    tempInvoiceNumber?: string;
    isInternalOrderData?: boolean;
    isOrderDeviations?: boolean;
    messages: OrderMessage[];
    isWarrantyOrder: boolean;
    linkedOrders?: LinkedOrder[];
    orderRef?: LinkedOrder;
    // DD-3482 Marketplace: the direct sub-orders this lab placed for the order.
    // The backend resolves it only for a viewer who is a party to both orders.
    subOrders?: Pick<Order, '_id' | 'orderId'>[];
    // DD-3484 Marketplace: the order this sub-order fulfils. The backend resolves it
    // only for the lab that created the sub-order (the sub-order's customer).
    parentOrder?: Pick<Order, '_id' | 'orderId'>;
    // DD-3482 / DD-3486 Marketplace: the sending lab's warnings about, and read-only
    // rows of, its direct sub-orders. Empty for anyone but the order's supplier.
    subOrderWarnings?: SubOrderWarning[];
    subOrderLines?: SubOrderLine[];
    // DD-3485 Marketplace: what this sub-order was created with. Resolves only for the
    // lab that created it (the sub-order's customer); null for everyone else.
    subcontractSelection?: SubcontractSelectionRecord | null;
    rejectStatus?: OrderRejectStatus;
    archived: Archived;
    rating: Rating;
    isRated: boolean;
    scannerCase?: ScannerCase;
    deliveryNotePdfUrl: string;
    transportTrackingLink?: string;
    version: number;
    originalOrder: Order;
    availableVersions: AvailableVersion[];
    filesUrl: DenthubFile[];
    // DD-2506: the unified restoration model.
    restorations: Restoration[];
    restorationTypes: RestorationType[];
    manualSelectedReceivedDate: Date;
    manualSelectedSentDate?: Date;
    // DD-3800: resolved by the backend for the Production › Overview click-through list.
    cameInVia?: OrderCameInVia;
    actionsCount?: number;
    orderTags?: OrderTag[];
    isCopyOrder?: boolean;
    iteration?: number;
    decline?: Decline[];
    cancel?: Cancel;
    isReadyForDelivery?: boolean;
    isDeliveryCreated?: boolean;
    isImportedOrder?: boolean;
    orderForms: OrderForm[];
    clinicOrderVersion: string;
    totalOriginalPrice?: number;
    shouldShowTruckButton?: boolean;
    isDraft?: boolean;
    actionSet: string;
    isClientEnabled: boolean;
    productionValueReports?: ProductionValueReport[];
    productionPlan?: {
        stepId: string;
        stepName: string;
        technicianId?: string;
        technicianName?: string;
        startDate?: Date | null;
        deadline?: Date | null;
        estimatedProductionHours?: number;
        estimatedMachineHours?: number;
        stepOrder?: number;
        allowOvernight?: boolean;
        startNextDay?: boolean;
        machineId?: string;
        departmentId?: string;
        affectedByQuantityModifier?: number;
        workUnits?: number;
    }[];
    panNumber?: string;
    panNumberDescription?: string;
    hasOrderFiles?: boolean;
    hasDeliveryNotePdf?: boolean;
};

export type prodAction = {
    actionId: string;
    name: string;
    number: string;
    productionDeductionValue?: number;
};

export type ProductionValueReport = {
    actions: prodAction[];
    templateName?: string;
    steps: ProductionStep[];
};

export type ProductionStep = {
    step: string;
    department: string;
    technician?: string;
    technicians?: User[];
    valuePercent: number;
    stepValue: number;
    stepDeductionValue?: number;
};

export type Decline = {
    by: string;
    message: string;
    declineOn: number;
};

export type Cancel = {
    by: string;
    message: string;
    on: number;
};

// --- DD-2506 unified restoration model (read side) -------------------------------------
// (`RestorationType` is the enum declared further down in this file.)

export enum TeethSelectionType {
    TEETH = 'TEETH',
    RANGE = 'RANGE',
    JAW = 'JAW',
    NONE = 'NONE',
}

export enum RestorationJaw {
    UPPER_JAW = 'UPPER_JAW',
    LOWER_JAW = 'LOWER_JAW',
    BOTH_JAWS = 'BOTH_JAWS',
}

export enum ToothState {
    PONTIC = 'PONTIC',
    EXTRACTED = 'EXTRACTED',
    MISSING = 'MISSING',
}

export enum RestorationPropertyValueType {
    SIMPLE_VALUE = 'SIMPLE_VALUE',
    VALUE_AND_LABEL = 'VALUE_AND_LABEL',
    LIST_OF_TEETH = 'LIST_OF_TEETH',
    BOOLEAN_VALUE = 'BOOLEAN_VALUE',
}

// Monetary figures are null when the backend withholds them from an external
// technician whose lab enabled LIMIT_INFO_EXTERNAL_USERS (DD-3493).
export type RestorationPrice = {
    quantity: number;
    unitPrice: number | null;
    vatPercent: number | null;
    totalWithoutVat: number | null;
    totalWithVat: number | null;
    originalUnitPrice: number | null;
    originalTotalWithVat: number | null;
};

export type RestorationActionCatalogue = {
    exists: boolean;
    quotable?: boolean | null;
    internalCode?: string | null;
    materialMacros: { _id: string; name: string }[];
};

export type RestorationAction = {
    _id: string;
    /** @deprecated migration aid: pairs the row with its legacy added action until step 3 */
    actionId?: string | null;
    code?: string | null;
    name: string;
    restorationType?: RestorationType | null;
    price: RestorationPrice;
    warranty: boolean;
    isTioTandvard: boolean;
    category?: string | null;
    categoryName?: string | null;
    /** Frozen with the row: the action set's quotable flag. */
    quotable?: boolean | null;
    /** Frozen with the row: the quantity follows the teeth. */
    costPerTooth?: boolean | null;
    /** Frozen with the row: the supplying lab's internal code; null for the clinic. */
    internalCode?: string | null;
    catalogue?: RestorationActionCatalogue | null;
};

export type RestorationMaterial = {
    _id: string;
    /** The catalogue material the row was placed from; a free-text row has none. */
    materialId?: string | null;
    relatedActionId?: string | null;
    code?: string | null;
    name: string;
    batchNumber?: string | null;
    /** The chosen material's price difference kept as a row; not an added material. */
    isMaterialSurcharge?: boolean | null;
    warranty: boolean;
    isTioTandvard: boolean;
    price: RestorationPrice;
};

// DD-3482 Marketplace: why a parent order shows a sub-order warning banner.
export enum SubOrderWarningKind {
    SUB_ORDER_STILL_ACTIVE = 'SUB_ORDER_STILL_ACTIVE',
    SUB_ORDER_DECLINED = 'SUB_ORDER_DECLINED',
    SUB_ORDER_CANCELLED = 'SUB_ORDER_CANCELLED',
}

export type SubOrderReference = {
    _id: string;
    orderId: string;
    currency?: string;
    supplier: { _id: string; name: string };
};

export type SubOrderWarning = {
    kind: SubOrderWarningKind;
    subOrder: SubOrderReference;
};

export enum SubOrderLineKind {
    ACTION = 'ACTION',
    MATERIAL = 'MATERIAL',
}

// DD-3486 Marketplace: a sub-order's action or material row, shown read-only on the
// parent order with the subcontractor's price. `restorationId` is the parent's
// restoration of the same type and teeth, null when none matches.
export type SubOrderLine = {
    subOrder: SubOrderReference;
    kind: SubOrderLineKind;
    restorationId?: string | null;
    restorationType: RestorationType;
    teeth: RestorationTeeth;
    code?: string | null;
    name: string;
    batchNumber?: string | null;
    price: RestorationPrice;
};

export type RestorationTeeth = {
    type: TeethSelectionType;
    selectedTeeth?: number[] | null;
    selectedRange?: { start: number; end: number } | null;
    selectedJaw?: RestorationJaw | null;
    toothStates?: { tooth: number; state: ToothState }[] | null;
};

export type RestorationActionUnit = {
    actionRowId: string;
    label: string;
    value: number;
};

export type RestorationProperty = {
    key: string;
    valueType: RestorationPropertyValueType;
    value: unknown;
    text?: string | null;
    flag?: boolean | null;
    teeth?: number[] | null;
    units?: RestorationActionUnit[] | null;
};

export type Restoration = {
    _id: string;
    type: RestorationType;
    actions: RestorationAction[];
    materials: RestorationMaterial[];
    teeth: RestorationTeeth;
    properties: RestorationProperty[];
    /** The restoration's own flags; unset on restorations derived from legacy orders. */
    warranty?: boolean | null;
    isTioTandvard?: boolean | null;
    legacyConstructionId?: string | null;
};

export type AvailableVersion = {
    version: number;
    createdAt: Date;
};

export type RatingReasons = {
    name: string;
    value: boolean;
};

export type Rating = {
    satisfied: boolean;
    notSatisfied: boolean;
    reasons: RatingReasons[];
    comments: string;
    by: User;
};

export type OrderMessage = {
    message: string;
    orderId?: string;
    type: string;
    deliveryDate?: number;
    readBy: string[];
    orderVersion: number;
    by: string;
    hasManualDateEntry?: boolean;
    messageOn: number;
    manualSelectedDate?: number;
};

export type OrderDeliveryDetails = {
    date: number;
    price: number;
    status: OrderDeliveryStatus;
    deadlineStatus?: OrderDeliveryStatus;
    sampleType: SampleType;
    sample?: Sample;
    isPickUpAtClinic: boolean;
    isLabHandleRequest: boolean;
    destination: Organization;
    pickUpLocation: Organization;
    isLabHaveOwnResource: boolean;
    reason?: Record<string, boolean>;
    time?: {
        hour?: number;
        minute?: number;
    };
    internalDeliveryDate: Date;
    externalDeliveryDate: Date;
};
export type Content = {
    name: string;
    value: boolean;
};

export type Sample = {
    type: string;
    otherContent: string;
    content?: Content[];
};

export type OrderPatient = {
    personalIdentityNumber: string;
    firstName: string;
    lastName: string;
    category?: string;
    noPersonalIdentityNumber?: boolean;
    gender?: string;
    patientCategory?: PatientCategory;
};

// DD-3485: the categories a sub-order was created with, recorded at creation.
export type SubcontractSelectionRecord = {
    restorationCount: number;
    attachmentCount: number;
    scanCaseFileCount: number;
    patientReference: boolean;
    patientIdentityNumber: boolean;
    dentistName: boolean;
    comment: boolean;
    confirmedAt?: string;
};

export type PatientCategory = {
    minage?: number;
    maxage?: number;
    name: string;
    translatedName?: string;
};

export type Message = {
    message: string;
    orderId?: string;
    type: string;
    messageOn: number;
};

export enum OrderType {
    SIMPLE_TYPE = 'SIMPLE_TYPE',
    ACTION_BASED = 'ACTION_BASED',
}

export enum OrderScope {
    MY_DRAFTS = 'MY_DRAFTS',
    ALL_DRAFTS = 'ALL_DRAFTS',
}

export enum DraftStatus {
    ALL = 'ALL',
    DRAFT = 'DRAFT',
    COMPLETED_DRAFT = 'COMPLETED_DRAFT',
}

export type DraftOrderFilter = {
    search?: string;
    scope?: OrderScope;
    status?: DraftStatus;
};

export enum CertifyListType {
    MY_CERTIFICATIONS = 'MY_CERTIFICATIONS',
    ALL_CERTIFICATIONS = 'ALL_CERTIFICATIONS',
}

export type UnCertifiedOrderFilter = {
    search?: string;
    myCertifications?: boolean;
};

export type OrderFilter = {
    search?: string;
    orderStatus?: string[];
    status?: string;
    sample?: SampleType;
    clinicId?: string | null;
    supplierId?: string | null;
    stepId?: string;
    orderTagIds?: string[];
    archived?: ArchivedFilterStatus;
    production?: ProductionStatus;
    dateFilterType?: string;
    dateFromTo?: DateRange<Date | null>;
    technicianId?: string | null;
    pauseReason?: string | null;
    isPaused?: boolean;
};

export enum OrderStatus {
    ALL = 'ALL',
    ACCEPTED_BY_LAB = 'ACCEPTED_BY_LAB',
    ORDER_APPROVED = 'ORDER_APPROVED',
    SENT_BY_CUSTOMER = 'SENT_BY_CUSTOMER',
    SENT_BY_LAB = 'SENT_BY_LAB',
    CANCELLED = 'CANCELLED',
    DECLINED = 'DECLINED',
    DRAFT = 'DRAFT',
    COMPLETED_DRAFT = 'COMPLETED_DRAFT',
}

export enum OrderDeliveryStatus {
    NO_REMARKS = 'NO_REMARKS',
    EARLY = 'EARLY',
    DELAYED = 'DELAYED',
}

export enum SampleType {
    ALL = 'ALL',
    NO_SAMPLE = 'NO_SAMPLE',
    PHYSICAL = 'PHYSICAL',
    DIGITAL = 'DIGITAL',
}

export enum OrderTableType {
    LAB,
    DENTIST,
    EXTERNAL_TECHNICIAN,
    LAB_WORKFLOW,
    EXTERNAL_TECHNICIAN_WORKFLOW,
    SUPPLIER,
    SUPPLIER_EXTERNAL_TECHNICIAN,
    SUPPLIER_EXTERNAL_TECHNICIAN_WORKFLOW,
}

export enum ArchivedFilterStatus {
    ONLY_ACTIVE = 'ONLY_ACTIVE',
    ONLY_ARCHIVED = 'ONLY_ARCHIVED',
    ALL = 'ALL',
}

export enum ProductionStatus {
    ALL = 'ALL',
    PRODUCTION_ONGOING = 'PRODUCTION_ONGOING',
    PRODUCTION_FINISHED = 'PRODUCTION_FINISHED',
}

export type ChatTemplate = {
    title: string;
    message: string;
};

export type LinkedOrder = {
    order: string;
    orderId: string;
    type: string;
};

export type OperationButtonsConfig = {
    unArchiveOrder: boolean;
    archiveOrder: boolean;
    rateOrder: boolean;
    cancelOrder: boolean;
    declineByLab: boolean;
    resendOrder: boolean;
};

export type StatusButtonsConfig = {
    approveOrder: boolean;
    acceptByLab: boolean;
    sentByLab: boolean;
};

export enum OrderListType {
    MY_ORDERS = 'MY_ORDERS',
    ALL_ORDERS = 'ALL_ORDERS',
    ON_CLINIC = 'ON_CLINIC',
}

export type DeliveryDate = {
    deliveryDate: string;
    isDeliveryDateAHoliday: boolean;
};

export type LabWithRanking = ConnectedLab & {
    labId: string;
    name: string;
    holidayDays: number[];
    openingHours: Hours[];
    profileImage: string;
    /** DD-2506: the selection priced for this lab exactly as a save would store it. */
    restorations: Restoration[];
    /** Incl. VAT. */
    totalPrice: number;
    totalVat: number;
    calculatedLabPrice: number;
    ratePercent: number;
    totalRating: number;
    rank: number;
    currency?: string;
    isRequireException: boolean;
    labExceptions: any[];
    deliveryDays: number;
    deliveryDate: DeliveryDate;
    isQuotable: boolean;
    contact: OrganizationContact;
    longDescription: string;
    unavailableLabDates: number[];
};

export enum RestorationType {
    // Digital lab services
    CAD = 'CAD',
    CAM_MILLING = 'CAM_MILLING',
    SURGICAL_GUIDE = 'SURGICAL_GUIDE',
    LAB_SERVICES_OTHER = 'LAB_SERVICES_OTHER',

    // Fixed prosthetics
    BRIDGE = 'BRIDGE',
    CROWN = 'CROWN',
    CROWN_BRIDGE = 'CROWN_BRIDGE',
    INLAY = 'INLAY',
    ONLAY = 'ONLAY',
    POST_CORE = 'POST_CORE',
    TEMPORARY_BRIDGE = 'TEMPORARY_BRIDGE',
    TEMPORARY_CROWN = 'TEMPORARY_CROWN',
    TEMPORARY_INLAY = 'TEMPORARY_INLAY',
    TEMPORARY_ONLAY = 'TEMPORARY_ONLAY',
    VENEER = 'VENEER',
    FIXED_PROSTHETICS_OTHER = 'FIXED_PROSTHETICS_OTHER',

    // Implant prosthetics
    ABUTMENT = 'ABUTMENT',
    IMPLANT_BRIDGE = 'IMPLANT_BRIDGE',
    IMPLANT_CROWN = 'IMPLANT_CROWN',
    IMPLANT_OVERDENTURE = 'IMPLANT_OVERDENTURE',
    TEMPORARY_IMPLANT_BRIDGE = 'TEMPORARY_IMPLANT_BRIDGE',
    TEMPORARY_IMPLANT_CROWN = 'TEMPORARY_IMPLANT_CROWN',
    IMPLANT_PROSTHETICS_OTHER = 'IMPLANT_PROSTHETICS_OTHER',

    // Orthodontics
    ALIGNER = 'ALIGNER',
    ANCHORAGE = 'ANCHORAGE',
    EXPANSION_PALATAL = 'EXPANSION_PALATAL',
    GROWTH_ADAPTATION = 'GROWTH_ADAPTATION',
    RETAINER = 'RETAINER',
    ORTHODONTICS_OTHER = 'ORTHODONTICS_OTHER',

    // Removable prosthetics
    DIAGNOSTIC_WAXUP = 'DIAGNOSTIC_WAXUP',
    RELINE = 'RELINE',
    CLASP = 'CLASP',
    FLEXIBLE_PARTIAL = 'FLEXIBLE_PARTIAL',
    FULL_DENTURE = 'FULL_DENTURE',
    PARTIAL_DENTURE = 'PARTIAL_DENTURE',
    TEMPORARY_FULL_DENTURE = 'TEMPORARY_FULL_DENTURE',
    TEMPORARY_PARTIAL_DENTURE = 'TEMPORARY_PARTIAL_DENTURE',
    REMOVABLE_PROSTHETICS_OTHER = 'REMOVABLE_PROSTHETICS_OTHER',

    // Guards and splints
    SPORTS_GUARD = 'SPORTS_GUARD',
    ANTIAPNEA = 'ANTIAPNEA',
    BITE_REGISTRATION = 'BITE_REGISTRATION',
    BLEACH_TRAY = 'BLEACH_TRAY',
    NIGHT_GUARD = 'NIGHT_GUARD',
    SPLINT = 'SPLINT',
    GUARDS_AND_SPLINTS_OTHER = 'GUARDS_AND_SPLINTS_OTHER',
}

export enum RestorationCategory {
    OTHER_LAB_SERVICES = 'OTHER_LAB_SERVICES',
    FIXED_PROSTHETICS = 'FIXED_PROSTHETICS',
    IMPLANT_PROSTHETICS = 'IMPLANT_PROSTHETICS',
    ORTHODONTICS = 'ORTHODONTICS',
    REMOVABLE_PROSTHETICS = 'REMOVABLE_PROSTHETICS',
    GUARDS_AND_SPLINTS = 'GUARDS_AND_SPLINTS',
}

const restorationTypeToCategoryMap: Record<RestorationType, RestorationCategory> = {
    // Other lab services
    [RestorationType.CAD]: RestorationCategory.OTHER_LAB_SERVICES,
    [RestorationType.CAM_MILLING]: RestorationCategory.OTHER_LAB_SERVICES,
    [RestorationType.SURGICAL_GUIDE]: RestorationCategory.OTHER_LAB_SERVICES,
    [RestorationType.LAB_SERVICES_OTHER]: RestorationCategory.OTHER_LAB_SERVICES,

    // Fixed prosthetics
    [RestorationType.BRIDGE]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.CROWN]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.CROWN_BRIDGE]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.INLAY]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.ONLAY]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.POST_CORE]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.TEMPORARY_BRIDGE]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.TEMPORARY_CROWN]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.TEMPORARY_INLAY]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.TEMPORARY_ONLAY]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.VENEER]: RestorationCategory.FIXED_PROSTHETICS,
    [RestorationType.FIXED_PROSTHETICS_OTHER]: RestorationCategory.FIXED_PROSTHETICS,

    // Implant prosthetics
    [RestorationType.ABUTMENT]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.IMPLANT_BRIDGE]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.IMPLANT_CROWN]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.IMPLANT_OVERDENTURE]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.TEMPORARY_IMPLANT_BRIDGE]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.TEMPORARY_IMPLANT_CROWN]: RestorationCategory.IMPLANT_PROSTHETICS,
    [RestorationType.IMPLANT_PROSTHETICS_OTHER]: RestorationCategory.IMPLANT_PROSTHETICS,

    // Orthodontics
    [RestorationType.ALIGNER]: RestorationCategory.ORTHODONTICS,
    [RestorationType.ANCHORAGE]: RestorationCategory.ORTHODONTICS,
    [RestorationType.EXPANSION_PALATAL]: RestorationCategory.ORTHODONTICS,
    [RestorationType.GROWTH_ADAPTATION]: RestorationCategory.ORTHODONTICS,
    [RestorationType.RETAINER]: RestorationCategory.ORTHODONTICS,
    [RestorationType.ORTHODONTICS_OTHER]: RestorationCategory.ORTHODONTICS,

    // Removable prosthetics
    [RestorationType.DIAGNOSTIC_WAXUP]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.RELINE]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.CLASP]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.FLEXIBLE_PARTIAL]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.FULL_DENTURE]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.PARTIAL_DENTURE]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.TEMPORARY_FULL_DENTURE]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.TEMPORARY_PARTIAL_DENTURE]: RestorationCategory.REMOVABLE_PROSTHETICS,
    [RestorationType.REMOVABLE_PROSTHETICS_OTHER]: RestorationCategory.REMOVABLE_PROSTHETICS,

    // Guards and splints
    [RestorationType.SPORTS_GUARD]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.ANTIAPNEA]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.BITE_REGISTRATION]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.BLEACH_TRAY]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.NIGHT_GUARD]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.SPLINT]: RestorationCategory.GUARDS_AND_SPLINTS,
    [RestorationType.GUARDS_AND_SPLINTS_OTHER]: RestorationCategory.GUARDS_AND_SPLINTS,
};

export function getRestorationCategory(type: RestorationType): RestorationCategory {
    return restorationTypeToCategoryMap[type];
}

export function getRestorationTypesByCategory(category: RestorationCategory): RestorationType[] {
    return Object.entries(restorationTypeToCategoryMap)
        .filter(([, cat]) => cat === category)
        .map(([type]) => type as RestorationType);
}
